"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _isEmpty = _interopRequireDefault(require("lodash/isEmpty"));

var _isNumber = _interopRequireDefault(require("lodash/isNumber"));

var _isString = _interopRequireDefault(require("lodash/isString"));

var _momentTimezone = _interopRequireDefault(require("moment-timezone"));

var _number = _interopRequireDefault(require("../number/number"));

var _string = _interopRequireDefault(require("../string/string"));

(function () {
  var enterModule = require('react-hot-loader').enterModule;

  enterModule && enterModule(module);
})();

/**
 * Utilities for working with times.
 * @module TimeUtils
 */
var TimeUtils = {
  /**
   * Normalizes the provided time down to the provided step.
   * @static
   * @function normalizeToStep
   * @param {String} time - The input value in the format hh:mm A.
   * @param {Number} [step=1] - The step to round down to.
   * @example
   * TimeUtils.normalizeToStep('07:07 PM', 15); // '07:00 PM'
   * @returns {String} - The normalized time.
   */
  normalizeToStep: function normalizeToStep(time) {
    var step = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;

    if ((0, _isEmpty.default)(time)) {
      return time;
    }

    if (!(0, _isString.default)(time)) {
      throw new Error('The supplied time is not a string.');
    }

    var _TimeUtils$getValuesF = TimeUtils.getValuesFromString(time, step),
        hours = _TimeUtils$getValuesF.hours,
        mins = _TimeUtils$getValuesF.mins;

    return TimeUtils.getFromValuesAsString(hours, mins);
  },

  /**
   * Provides the hours, minutes, and meridiem from a time string.
   * @static
   * @function getValuesFromString
   * @param {String} time - The input value in the format hh:mm A.
   * @param {Number} [step=1] - The step to round down to.
   * @example
   * TimeUtils.getValuesFromString('10:19 AM');
   * @returns {Object} - The object containing the hours, minutes, and meridiem.
   */
  getValuesFromString: function getValuesFromString(time) {
    var step = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;

    if ((0, _isEmpty.default)(time)) {
      return time;
    }

    if (!(0, _isString.default)(time)) {
      throw new Error('The supplied time is not a string.');
    }

    var firstPartials = time.split(':');
    var secondPartials = firstPartials[1].split(' ');
    var isAM = secondPartials[1] === 'AM';
    var mins = parseInt(secondPartials[0], 10);

    var minsRounded = _number.default.roundDownToNearestStep(mins, step);

    var hours = parseInt(firstPartials[0], 10);

    if (isAM && hours === 12) {
      hours = 0;
    } else if (!isAM && hours !== 12) {
      hours += 12;
    }

    return {
      hours: hours,
      mins: minsRounded,
      isAM: isAM
    };
  },

  /**
   * Provides the formatted time from hours and minutes values.
   * @static
   * @function getFromValuesAsString
   * @param {Number} hours - The hours in a 24 hour format.
   * @param {Number} minutes - The minutes from 0 - 60.
   * @param {Boolean} [roundMinutesDown=false] - Whether to round the minutes down to the provided step.
   * @param {Number} [step=1] - The step to round minutes down to.
   * @example
   * TimeUtils.getFromValuesAsString(20, 35); // '08:35 PM'
   * @returns {String} - The formatted time.
   */
  getFromValuesAsString: function getFromValuesAsString(hours, minutes) {
    var roundMinutesDown = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    var step = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 1;
    var meridiem = hours < 12 ? 'AM' : 'PM';
    var newHours = hours;
    var newMinutes = minutes;

    if (newHours > 12) {
      newHours -= 12;
    } else if (newHours === 0) {
      newHours = 12;
    }

    if (roundMinutesDown) {
      newMinutes = _number.default.roundDownToNearestStep(newMinutes, step);
    }

    return "".concat(_string.default.padWith(newHours), ":").concat(_string.default.padWith(newMinutes), " ").concat(meridiem);
  },

  /**
   * Converts a mobile input time to a string in the format hh:mm A.
   * @static
   * @function getMobileAsString
   * @param {String} time - The input value in the format HH:mm (24 hour format).
   * @param {Number} [step=1] - The step to round down to.
   * @example
   * TimeUtils.getMobileAsString('22:19'); // 10:19 PM
   * @returns {String} - The formatted time.
   */
  getMobileAsString: function getMobileAsString(time) {
    var step = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;

    if (!(0, _isString.default)(time)) {
      throw new Error('The supplied time is not a string.');
    }

    var frags = time.split(':');
    var mins = parseInt(frags[1], 10);

    var minsRounded = _number.default.roundDownToNearestStep(mins, step);

    var hours = parseInt(frags[0], 10);
    var meridiem = hours < 12 ? 'AM' : 'PM';
    var hh = hours;

    if (hours > 12) {
      hh -= 12;
    } else if (hours === 0) {
      hh = 12;
    }

    return "".concat(_string.default.padWith(hh), ":").concat(_string.default.padWith(minsRounded), " ").concat(meridiem);
  },

  /**
   * Converts a time to a date object.
   * @static
   * @function getAsDate
   * @param {String} time - The input value in the format hh:mm A.
   * @example
   * TimeUtils.getAsDate('10:19 PM');
   * @returns {Date} - The time inside of a date object.
   */
  getAsDate: function getAsDate(time) {
    if ((0, _isEmpty.default)(time)) {
      return null;
    }

    if (!(0, _isString.default)(time)) {
      throw new Error('The supplied time is not a string.');
    }

    var _TimeUtils$getValuesF2 = TimeUtils.getValuesFromString(time),
        hours = _TimeUtils$getValuesF2.hours,
        mins = _TimeUtils$getValuesF2.mins;

    var d = new Date();
    d.setHours(hours);
    d.setMinutes(mins);
    d.setSeconds(0);
    return d;
  },

  /**
   * Converts a time to a moment instance.
   * @static
   * @function getAsMoment
   * @param {String} time - The input value in the format hh:mm A.
   * @example
   * TimeUtils.getAsMoment('10:19 PM');
   * @returns {Moment} - The time inside of a moment instance.
   */
  getAsMoment: function getAsMoment(time) {
    if ((0, _isEmpty.default)(time)) {
      return null;
    }

    if (!(0, _isString.default)(time)) {
      throw new Error('The supplied time is not a string.');
    }

    var _TimeUtils$getValuesF3 = TimeUtils.getValuesFromString(time),
        hours = _TimeUtils$getValuesF3.hours,
        mins = _TimeUtils$getValuesF3.mins;

    return (0, _momentTimezone.default)([1, 1]).startOf('day').add(hours, 'hours').add(mins, 'minutes');
  },

  /**
   * Generate an array of times.
   * @static
   * @function getTimes
   * @param {Object} data - The data to pass to the function.
   * @param {Number} [data.step=30] - Increment times by this many minutes.
   * @param {String} [data.format='hh:mm A'] - Moment.js format (http://momentjs.com/docs/#/displaying/format/).
   * @param {String} [data.ignoreBefore] - Number of minutes to ignore times before in the output.
   * @param {String} [data.ignoreAfter] - Number of minutes to ignore times after in the output.
   * @example
   * TimeUtils.getTimes({
   *     step: 30,
   *     format: 'hh:mm A'
   * });
   * @returns {Array} times - An array of times.
   */
  getTimes: function getTimes() {
    var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        _ref$step = _ref.step,
        step = _ref$step === void 0 ? 30 : _ref$step,
        _ref$format = _ref.format,
        format = _ref$format === void 0 ? 'hh:mm A' : _ref$format,
        ignoreBefore = _ref.ignoreBefore,
        ignoreAfter = _ref.ignoreAfter;

    if (ignoreBefore && !(0, _isNumber.default)(ignoreBefore)) {
      throw new Error('The ignoreBefore value passed was not a number.');
    }

    if (ignoreAfter && !(0, _isNumber.default)(ignoreAfter)) {
      throw new Error('The ignoreAfter value passed was not a number.');
    }

    if (ignoreBefore && ignoreAfter && ignoreBefore > ignoreAfter) {
      throw new Error('The value of ignoreBefore should be less than the value of ignoreAfter.');
    }

    var dateTime = (0, _momentTimezone.default)([0, 0]);
    var total = 1440;
    var len = total / step;
    var times = [];
    var beforeMoment = ignoreBefore ? dateTime.clone().startOf('day').add(ignoreBefore, 'minutes') : null;
    var afterMoment = ignoreAfter ? dateTime.clone().startOf('day').add(ignoreAfter, 'minutes') : null;
    var i = 0;
    var id = 0;
    var shouldAdd = true;
    var time;

    for (i; i < len; i++) {
      id = i * step;
      time = dateTime.clone().startOf('day').add(id, 'minutes');
      shouldAdd = beforeMoment ? time.isSameOrAfter(beforeMoment) : shouldAdd;

      if (shouldAdd) {
        shouldAdd = afterMoment ? time.isSameOrBefore(afterMoment) : shouldAdd;
      }

      if (shouldAdd) {
        times.push({
          id: id,
          formatted: time.format(format)
        });
      }
    }

    return times;
  }
};
var _default = TimeUtils;
var _default2 = _default;
exports.default = _default2;
;

(function () {
  var reactHotLoader = require('react-hot-loader').default;

  var leaveModule = require('react-hot-loader').leaveModule;

  if (!reactHotLoader) {
    return;
  }

  reactHotLoader.register(TimeUtils, "TimeUtils", "/Users/mprzybylski/Work/SpotHero/fe-utils/src/time/time.js");
  reactHotLoader.register(_default, "default", "/Users/mprzybylski/Work/SpotHero/fe-utils/src/time/time.js");
  leaveModule(module);
})();

;